import { useState } from 'react'
import { useCommenterAuth } from '../hooks/useCommenterAuth'
import {X} from 'lucide-react'

interface UserLoginProps {
  onClose: () => void
  onSuccess?: () => void
}

export function UserLogin({ onClose, onSuccess }: UserLoginProps) {
  const [isRegistering, setIsRegistering] = useState(false)
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    nickname: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  
  const { register, login, loading, error } = useCommenterAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      if (isRegistering) {
        await register({
          username: formData.username,
          password: formData.password,
          email: formData.email,
          nickname: formData.nickname || formData.username
        })
      } else {
        await login({
          username: formData.username,
          password: formData.password
        })
      }
      
      onSuccess?.()
      onClose()
    } catch (err) {
      // Error handled by hook
      console.error('Auth error:', err)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl w-full max-w-md border border-blue-500/20">
        {/* Header with Logo */}
        <div className="flex flex-col items-center p-6 border-b border-slate-700">
          <img 
            src="https://static.lumi.new/e3/e39f2b464939bf84da8bfe117fca0e35.webp" 
            alt="Base Builders Logo" 
            className="w-16 h-16 rounded-xl object-cover mb-4"
          />
          <div className="flex items-center justify-between w-full">
            <h2 className="text-2xl font-bold text-white">
              {isRegistering ? 'Create Account' : 'Welcome Back'}
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Username
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
              placeholder="Enter username"
            />
          </div>

          {/* Email (Registration only) */}
          {isRegistering && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                placeholder="Enter email"
              />
            </div>
          )}

          {/* Nickname (Registration only) */}
          {isRegistering && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Display Name <span className="text-slate-500">(optional)</span>
              </label>
              <input
                type="text"
                name="nickname"
                value={formData.nickname}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                placeholder="How others will see you"
              />
            </div>
          )}

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-sm"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {isRegistering && (
              <p className="text-xs text-slate-500 mt-1">Must be at least 6 characters</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-500 hover:to-cyan-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-blue-500/50"
          >
            {loading ? 'Please wait...' : isRegistering ? 'Create Account' : 'Sign In'}
          </button>

          {/* Toggle Mode */}
          <div className="text-center pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={() => {
                setIsRegistering(!isRegistering)
                setFormData({ username: '', password: '', email: '', nickname: '' })
              }}
              className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
            >
              {isRegistering 
                ? 'Already have an account? Sign in' 
                : "Don't have an account? Create one"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
